// Components
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import {
  IconAlertTriangle,
  IconCheck,
  IconCloudUpload,
  IconFileUpload,
  IconFileTypePdf,
  IconInfoCircle,
  IconLink,
  IconUpload,
  IconUserSearch,
  IconX,
} from "@tabler/icons-react";

// Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  useExtractReferralPdf,
  type ExtractedPatientInfo,
  type PotentialDuplicate,
  type ReferralExtractResult,
} from "@/hooks/domain/useExtractReferralPdf";

// Utils
import { parseFhirInstantForDisplay } from "@benchmark/ui/utils";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";

// Styles
import styles from "./ReferralUploadModalContent.module.css";

/**
 * Input mode for referral extraction source.
 * @agentic connections
 */
type InputMode = "file" | "url";

/**
 * Data returned to patient-creation flow after successful extraction.
 * @agentic connections
 */
export type ReferralExtractionPayload = {
  extractedInfo: ExtractedPatientInfo;
  binaryId: string;
  binaryUrl: string;
};

/**
 * Props for referral upload modal body.
 * @agentic connections
 */
export type ReferralUploadModalContentProps = {
  onExtracted: (data: ReferralExtractionPayload) => void;
  onCancel: () => void;
  onBusyChange?: (isBusy: boolean) => void;
  onDuplicateWarningChange?: (isShowing: boolean) => void;
};

/**
 * Pending extraction result while duplicate warnings are displayed.
 * @agentic connections
 */
type PendingResult = ReferralExtractionPayload & {
  potentialDuplicates: PotentialDuplicate[];
};

/**
 * Allowed Dialpad URL patterns for referral extraction.
 * @agentic connections
 */
const DIALPAD_URL_PATTERNS = [
  /^https:\/\/dialpad\.com\/blob\/.+$/,
  /^https:\/\/dialpad\.com\/blob-server\/.+$/,
];

/**
 * Validates whether the cleaned URL matches supported Dialpad patterns.
 * @agentic connections
 */
const isValidDialpadUrl = (url: string): boolean =>
  DIALPAD_URL_PATTERNS.some((pattern) => pattern.test(url));

/**
 * Removes query params from Dialpad URL before validation/submission.
 * @agentic connections
 */
const cleanDialpadUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch {
    return url;
  }
};

/**
 * Formats DOB for duplicate matching and extracted-summary display.
 * @agentic connections
 */
const formatDob = (dob?: string): string => {
  if (!dob) return "Unknown";
  try {
    return parseFhirInstantForDisplay(dob).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dob;
  }
};

/**
 * Referral upload and extraction UI rendered inside the shared modal shell.
 * @agentic connections
 */
export const ReferralUploadModalContent = ({
  onExtracted,
  onCancel,
  onBusyChange,
  onDuplicateWarningChange,
}: ReferralUploadModalContentProps) => {
  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<InputMode>("file");
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [pendingResult, setPendingResult] = useState<PendingResult | null>(
    null,
  );
  const { extractReferral, extractFromUrl, isExtracting } =
    useExtractReferralPdf();

  useEffect(() => {
    onBusyChange?.(isExtracting);
  }, [isExtracting, onBusyChange]);

  useEffect(
    () => () => {
      onBusyChange?.(false);
    },
    [onBusyChange],
  );

  useEffect(() => {
    onDuplicateWarningChange?.(pendingResult !== null);
  }, [onDuplicateWarningChange, pendingResult]);

  useEffect(
    () => () => {
      onDuplicateWarningChange?.(false);
    },
    [onDuplicateWarningChange],
  );

  const resetFormState = useCallback(() => {
    setFile(null);
    setUrl("");
    setUrlError(null);
    setPendingResult(null);
    setInputMode("file");
  }, []);

  const handleExtractionResult = useCallback(
    (result: ReferralExtractResult) => {
      if (!result.success) {
        showErrorNotification(result.error || "Failed to extract referral");
        return;
      }

      if (!result.extractedInfo) {
        showErrorNotification(
          "Could not extract patient information from the PDF. Please enter details manually.",
        );
        resetFormState();
        onCancel();
        return;
      }

      if (!result.binaryId || !result.binaryUrl) {
        showErrorNotification("Failed to upload document. Please try again.");
        return;
      }

      if (result.potentialDuplicates && result.potentialDuplicates.length > 0) {
        setPendingResult({
          extractedInfo: result.extractedInfo,
          binaryId: result.binaryId,
          binaryUrl: result.binaryUrl,
          potentialDuplicates: result.potentialDuplicates,
        });
        return;
      }

      showSuccessNotification("Referral extracted successfully");
      onExtracted({
        extractedInfo: result.extractedInfo,
        binaryId: result.binaryId,
        binaryUrl: result.binaryUrl,
      });
      resetFormState();
    },
    [onCancel, onExtracted, resetFormState],
  );

  const handleProceedAnyway = useCallback(() => {
    if (!pendingResult) return;

    showSuccessNotification("Referral extracted successfully");
    onExtracted({
      extractedInfo: pendingResult.extractedInfo,
      binaryId: pendingResult.binaryId,
      binaryUrl: pendingResult.binaryUrl,
    });
    resetFormState();
  }, [onExtracted, pendingResult, resetFormState]);

  const handleViewExistingPatient = useCallback(
    (patientId: string) => {
      resetFormState();
      onCancel();
      navigate(`/Patient/${patientId}`);
    },
    [navigate, onCancel, resetFormState],
  );

  const handleBackToUpload = useCallback(() => {
    setPendingResult(null);
  }, []);

  const handleCancel = useCallback(() => {
    if (isExtracting) return;
    resetFormState();
    onCancel();
  }, [isExtracting, onCancel, resetFormState]);

  const handleFileChange = useCallback((newFile: File | null) => {
    if (newFile && newFile.type !== "application/pdf") {
      showErrorNotification("Please select a PDF file");
      return;
    }
    setFile(newFile);
  }, []);

  const handleFileDrop = useCallback(
    (files: File[]) => {
      const droppedFile = files[0];
      if (!droppedFile) return;
      handleFileChange(droppedFile);
    },
    [handleFileChange],
  );

  const handleFileReject = useCallback(() => {
    showErrorNotification("Please upload a PDF file (max size 10MB)");
  }, []);

  const handleUrlChange = useCallback((value: string) => {
    setUrl(value);
    if (value) {
      setUrlError(null);
    }
  }, []);

  const handleModeChange = useCallback((value: string) => {
    setInputMode(value as InputMode);
    setFile(null);
    setUrl("");
    setUrlError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (inputMode === "file") {
      if (!file) return;

      try {
        const result = await extractReferral(file);
        handleExtractionResult(result);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to extract referral";
        showErrorNotification(message);
      }
      return;
    }

    if (!url.trim()) return;

    if (!isValidDialpadUrl(cleanDialpadUrl(url.trim()))) {
      setUrlError(
        "Invalid URL format. Please enter a valid Dialpad URL (https://dialpad.com/blob/... or https://dialpad.com/blob-server/...).",
      );
      return;
    }

    try {
      const result = await extractFromUrl(url.trim());
      handleExtractionResult(result);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to extract referral from URL";
      showErrorNotification(message);
    }
  }, [
    extractFromUrl,
    extractReferral,
    file,
    handleExtractionResult,
    inputMode,
    url,
  ]);

  const canSubmit = inputMode === "file" ? Boolean(file) : Boolean(url.trim());
  const showDuplicateWarning = pendingResult !== null;

  if (showDuplicateWarning) {
    return (
      <Stack gap="md">
        <Alert
          icon={<IconAlertTriangle size={16} />}
          color="yellow"
          title="Possible Duplicate Member"
        >
          A member with similar information already exists in the system. Please
          review before creating a new record.
        </Alert>

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Extracted from referral:
          </Text>
          <Text size="sm" c="dimmed">
            {pendingResult?.extractedInfo.patientName?.given}{" "}
            {pendingResult?.extractedInfo.patientName?.family}
            {pendingResult?.extractedInfo.dateOfBirth && (
              <>
                {" "}
                &bull; DOB: {formatDob(pendingResult.extractedInfo.dateOfBirth)}
              </>
            )}
          </Text>
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Existing member(s) found:
          </Text>
          {pendingResult?.potentialDuplicates.map((duplicate) => (
            <Alert
              key={duplicate.patientId}
              icon={<IconUserSearch size={16} />}
              color="gray"
              className={styles.duplicateMatchAlert}
            >
              <Group justify="space-between" align="flex-start">
                <Stack gap={2}>
                  <Text size="sm" fw={500}>
                    {duplicate.displayName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    DOB: {formatDob(duplicate.dateOfBirth)} &bull; Match:{" "}
                    {duplicate.matchType} ({duplicate.confidence})
                  </Text>
                </Stack>
                <Anchor
                  size="sm"
                  onClick={() => handleViewExistingPatient(duplicate.patientId)}
                >
                  View Member
                </Anchor>
              </Group>
            </Alert>
          ))}
        </Stack>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            onClick={handleBackToUpload}
            leftSection={<IconX size={16} />}
          >
            Back
          </Button>
          <Button
            color="yellow"
            onClick={handleProceedAnyway}
            leftSection={<IconCheck size={16} />}
          >
            Create Anyway
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        Upload a referral PDF or paste a Dialpad fax URL to automatically
        extract patient information. The document will be saved and linked to
        the patient record.
      </Alert>

      <SegmentedControl
        value={inputMode}
        onChange={handleModeChange}
        disabled={isExtracting}
        className={styles.sourceSegmentedControl}
        data={[
          {
            value: "file",
            label: (
              <Group gap="xs">
                <IconFileUpload size={14} />
                <span>Upload File</span>
              </Group>
            ),
          },
          {
            value: "url",
            label: (
              <Group gap="xs">
                <IconLink size={14} />
                <span>Paste URL</span>
              </Group>
            ),
          },
        ]}
        fullWidth
      />

      {inputMode === "file" ? (
        <>
          <Dropzone
            onDrop={handleFileDrop}
            onReject={handleFileReject}
            maxSize={10 * 1024 * 1024}
            accept={[MIME_TYPES.pdf]}
            multiple={false}
            disabled={isExtracting}
            className={styles.uploadDropzone}
            aria-label="Drop referral PDF file"
          >
            <Group
              justify="center"
              align="center"
              className={styles.dropzoneInner}
              gap="md"
            >
              <Dropzone.Accept>
                <IconUpload size={28} color="var(--mantine-color-green-6)" />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={28} color="var(--mantine-color-red-6)" />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconCloudUpload
                  size={28}
                  color="var(--mantine-color-purple-6)"
                />
              </Dropzone.Idle>

              <Stack gap={2} align="center">
                <Text size="sm" fw={600}>
                  Drag and drop referral PDF here
                </Text>
                <Text size="xs" c="dimmed">
                  or click to browse from your computer
                </Text>
              </Stack>
            </Group>
          </Dropzone>

          {file && (
            <Group justify="space-between" align="center">
              <Group gap="xs" wrap="nowrap">
                <IconFileTypePdf size={16} color="var(--mantine-color-red-6)" />
                <Text size="sm" c="dimmed" className={styles.selectedFile}>
                  Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Text>
              </Group>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => handleFileChange(null)}
                disabled={isExtracting}
              >
                Clear
              </Button>
            </Group>
          )}
        </>
      ) : (
        <TextInput
          label="Dialpad Fax URL"
          placeholder="Paste Dialpad fax URL here"
          value={url}
          onChange={(event) => handleUrlChange(event.currentTarget.value)}
          disabled={isExtracting}
          error={urlError}
          required
          aria-label="Enter Dialpad fax URL"
          description="Paste the fax URL from Dialpad"
        />
      )}

      {isExtracting && (
        <Group gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            {inputMode === "url"
              ? "Fetching and extracting patient information..."
              : "Extracting patient information..."}
          </Text>
        </Group>
      )}

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          onClick={handleCancel}
          disabled={isExtracting}
          leftSection={<IconX size={16} />}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpload}
          disabled={!canSubmit || isExtracting}
          loading={isExtracting}
          leftSection={!isExtracting ? <IconCheck size={16} /> : undefined}
        >
          Extract &amp; Pre-fill Form
        </Button>
      </Group>
    </Stack>
  );
};
